/**
 * The instrument colour system — how a measured field becomes colour.
 *
 * This is a DELIBERATE, SCOPED exception to the one-hue rule that governs the
 * rest of the scene (see web/src/components/room/scene/constants.ts). That rule
 * was written about the room: paper ground, ink hairlines, objects as outlined
 * paper, the seat as the single sanctioned hue. It still holds, unchanged. What
 * changes is that a *measured field* — sound pressure, reflected energy,
 * treatment priority — is no longer drawn in the room's ink. It is drawn as an
 * instrument layer laid over the drawing, the same relationship acoustic
 * simulation software has between its grey wireframe model and its coloured
 * mapping surface.
 *
 * The reason is dynamic range. `heatColor`'s paper->ink ramp resolves maybe four
 * or five steps once it is composited at partial opacity over a wall; a field
 * carrying 30 dB has nowhere to put the other 25. One-dimensional ink is the
 * right answer for a mark that means "here"; it is the wrong answer for a mark
 * that means "how much".
 *
 * ── About Turbo, honestly ────────────────────────────────────────────────────
 *
 * The standing objection to rainbow ramps is real and this file does not pretend
 * otherwise: hue is unordered, so a reader cannot say whether orange is more or
 * less than green without consulting the legend. Turbo does NOT fix that. It is
 * not monotonic in perceived lightness — `turbo(0.05)` and `turbo(0.95)` sit at
 * almost the same Oklab L (0.41 and 0.44), which is exactly the greyscale
 * collapse the objection describes, and `fieldColormap.test.ts` asserts that
 * shape rather than papering over it.
 *
 * What Turbo does fix is Jet's other defect, the one that actually misleads:
 * Jet has near-duplicate colours at genuinely different values and false
 * banding at its cyan and yellow transitions, so it invents structure that is
 * not in the data. Turbo is injective across its range and smooth through those
 * bands.
 *
 * So the ordering has to be carried by something else, and that something is
 * CONTOURS — isolines drawn at the legend's own tick values (see
 * `levelScale.ts`'s `contourLevels`). They survive greyscale, colour deficiency
 * and a bad monitor, because they are geometry rather than hue. This is not
 * decoration that can be dropped for performance later: without the contours
 * this ramp genuinely is worse than ink for a reader who cannot separate the
 * hues. Anything that paints a field with `turbo` owes its viewer contours.
 *
 * `divergent` has no such problem — it is symmetric in lightness by
 * construction, so magnitude reads correctly in greyscale even though direction
 * does not.
 */

import { type Rgb } from './color';

// ── Turbo ────────────────────────────────────────────────────────────────────

/**
 * Anton Mikhailov's degree-5 polynomial fit to Turbo, published by Google
 * alongside the colormap itself. Held as DATA rather than as two hand-written
 * implementations because `TURBO_GLSL` below is generated from these exact
 * numbers — the CPU path and the shader path cannot drift, because there is
 * only one set of coefficients and one of them is printed into the other.
 *
 * `lo` multiplies (1, x, x^2, x^3); `hi` multiplies (x^4, x^5).
 */
export const TURBO_POLY = {
  r: { lo: [0.13572138, 4.6153926, -42.66032258, 132.13108234], hi: [-152.94239396, 59.28637943] },
  g: { lo: [0.09140261, 2.19418839, 4.84296658, -14.18503333], hi: [4.27729857, 2.82956604] },
  b: { lo: [0.1066733, 12.64194608, -60.58204836, 110.36276771], hi: [-89.90310912, 27.34824973] },
} as const;

type Channel = { lo: readonly number[]; hi: readonly number[] };

function evalChannel({ lo, hi }: Channel, x: number): number {
  const x2 = x * x;
  const x3 = x2 * x;
  return (
    lo[0] + lo[1] * x + lo[2] * x2 + lo[3] * x3
    + hi[0] * x2 * x2 + hi[1] * x3 * x2
  );
}

/**
 * The polynomial overshoots [0,1] slightly (R peaks at ~1.03 near t=0.70, B
 * dips to ~-0.03 at t=1) — inherent to a degree-5 fit, and why the reference
 * GLSL wraps it in `saturate`. Clamp on both paths, never one.
 *
 * NaN maps to 0 rather than falling through. The naive `v < 0 ? 0 : v > 1 ? 1
 * : v` returns NaN unchanged (both comparisons are false), which survives
 * `Math.round` and lands as a NaN colour channel — a colour that paints as
 * black, as transparent, or as nothing at all depending on which consumer gets
 * it, with no error anywhere. These functions exist to be fed field data, and
 * field data has holes in it: a masked voxel, a zero-energy arrival, a
 * division by an empty reference. Failing to bare paper is the honest answer.
 */
const clamp01 = (v: number) => (Number.isFinite(v) ? (v < 0 ? 0 : v > 1 ? 1 : v) : 0);

/**
 * Magnitude -> colour for a measured field. `t` is a normalised ramp position,
 * NOT a raw physical value — put the physics through `levelScale.ts` first so
 * the legend and the pixels are anchored to the same window.
 */
export function turbo(t: number): Rgb {
  const x = clamp01(t);
  return {
    r: Math.round(clamp01(evalChannel(TURBO_POLY.r, x)) * 255),
    g: Math.round(clamp01(evalChannel(TURBO_POLY.g, x)) * 255),
    b: Math.round(clamp01(evalChannel(TURBO_POLY.b, x)) * 255),
  };
}

// ── Diverging ────────────────────────────────────────────────────────────────

/**
 * Oklab anchors for the difference ramp, as (L, chroma, hue°). Lightness is
 * symmetric about the neutral centre on purpose: it makes |change| readable in
 * greyscale, and it puts "nothing moved" at very nearly the page colour so an
 * untreated region of a before/after view reads as bare paper rather than as a
 * measurement.
 *
 * Not built from `palette.accent` / a danger token: those are chosen for
 * legibility as UI, not lightness-matched to each other, and a diverging ramp
 * whose two arms have different lightness reports a magnitude difference that
 * is not in the data.
 */
const DIVERGENT_ANCHORS = {
  cool: { l: 0.45, c: 0.135, h: 264 },
  neutral: { l: 0.93, c: 0.006, h: 264 },
  warm: { l: 0.45, c: 0.15, h: 29 },
} as const;

/** Oklab rectangular (L, a, b). Interpolating in a/b rather than chroma/hue is
 *  what keeps the cool->warm path from sweeping the long way round the hue
 *  circle and passing through green on its way to red. Exported (alongside
 *  `THERMAL_ANCHORS`) so `THERMAL_GLSL`'s baked anchor literals can be
 *  checked against this SAME conversion in tests, rather than a second,
 *  independently-written copy of the trig — the exact anti-drift discipline
 *  `TURBO_POLY`/`TURBO_GLSL` already established. */
export function anchorToLab({ l, c, h }: { l: number; c: number; h: number }): [number, number, number] {
  const rad = (h * Math.PI) / 180;
  return [l, c * Math.cos(rad), c * Math.sin(rad)];
}

const toLinear = (v: number) => (v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055);
const to8Bit = (v: number) => Math.min(255, Math.max(0, Math.round(toLinear(v) * 255)));

function labToRgb([L, A, B]: [number, number, number]): Rgb {
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3;
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3;
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3;
  return {
    r: to8Bit(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    g: to8Bit(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    b: to8Bit(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  };
}

/**
 * Piecewise-linear interpolation through three Oklab anchors: `low`..`mid`
 * for the bottom half of `t`, `mid`..`high` for the top half. The shared core
 * of both diverging ramps this file exports — the only thing that differs
 * between `divergent` (fades to page-neutral in the middle) and `thermal`
 * (stays saturated all the way through, on purpose — see its own header) is
 * which three anchors go in.
 */
function lerpLab3(
  low: [number, number, number], mid: [number, number, number], high: [number, number, number],
  t: number,
): Rgb {
  const x = clamp01(t);
  const [from, to, k] = x < 0.5 ? [low, mid, x / 0.5] : [mid, high, (x - 0.5) / 0.5];
  return labToRgb([
    from[0] + (to[0] - from[0]) * k,
    from[1] + (to[1] - from[1]) * k,
    from[2] + (to[2] - from[2]) * k,
  ]);
}

/**
 * Signed change -> colour, for after-minus-before views. `t` = 0.5 is "no
 * change" and must be mapped there by the caller's scale, not assumed —
 * `levelScale.ts`'s `signedToRamp` is what pins zero to the centre.
 *
 * Cool = the lower end of the signed range, warm = the upper. Which direction
 * counts as "better" is the caller's semantics, not this function's: quieter is
 * an improvement for reflected energy and a regression for a null at the seat.
 */
export function divergent(t: number): Rgb {
  return lerpLab3(
    anchorToLab(DIVERGENT_ANCHORS.cool),
    anchorToLab(DIVERGENT_ANCHORS.neutral),
    anchorToLab(DIVERGENT_ANCHORS.warm),
    t,
  );
}

// ── Thermal ──────────────────────────────────────────────────────────────────

/**
 * Oklch anchors for the volumetric pressure field and anything else keying
 * colour to an unsigned magnitude that should read as "cool" at one end and
 * "hot" at the other, with a real colour in between rather than a fade to
 * neutral.
 *
 * This is deliberately NOT `divergent`'s anchor set reused: `divergent`'s
 * middle is near-page-white on purpose, because "no change" is meant to
 * vanish into the drawing. Here there is no "no change" — the low end of a
 * pressure field is still a real, meaningful reading (quiet, not absent), so
 * it gets a real colour (a light, clean blue) instead of fading toward
 * transparency; that job belongs to `fieldAlpha`/the caller's own alpha
 * curve, not to the colour ramp collapsing into the background. Lightness is
 * intentionally NOT symmetric the way `divergent`'s is — `low` is lit deliberately
 * pale ("light blue" is the whole ask), `mid`/`high` sit close together so a
 * loud peak reads as saturated red without needing to go dark to get there.
 */
export const THERMAL_ANCHORS = {
  low: { l: 0.80, c: 0.085, h: 228 },
  mid: { l: 0.52, c: 0.19, h: 322 },
  high: { l: 0.58, c: 0.19, h: 27 },
} as const;

/**
 * Unsigned magnitude -> colour: light blue at 0, purple at 0.5, red at 1.
 *
 * Built for the volumetric pressure cloud, whose wire format only ever
 * carries `|P|` (see VolumetricPressure.tsx's own header) — there is no
 * true sign to key off, so "negative"/"positive" here means the quiet and
 * loud ends of the SAME magnitude scale, not a phase measurement. The point
 * is to give the quiet end a real, contrasting colour instead of letting it
 * fade to nothing, so the room reads as filled with data rather than empty
 * wherever it's calm.
 */
export function thermal(t: number): Rgb {
  return lerpLab3(
    anchorToLab(THERMAL_ANCHORS.low),
    anchorToLab(THERMAL_ANCHORS.mid),
    anchorToLab(THERMAL_ANCHORS.high),
    t,
  );
}

// ── Arrival ──────────────────────────────────────────────────────────────────

/**
 * Oklch anchors for ARRIVAL ENERGY: how much a reflected path contributes at
 * the seat. Green at the quiet end, amber through the middle, red at the loud
 * end — the traffic-light reading, chosen (2026-09-17, owner) over `thermal`'s
 * blue->purple->red because for this one quantity the ends already mean
 * "fine" and "deal with this", and an audience reading a room for the first
 * time reads that before it reads any legend.
 *
 * Lightness is not monotonic across it, and that is a real limitation the
 * `fieldColormap` header spells out for every hue ramp: in greyscale, green
 * and red land close together. What carries the order here is SIZE — the
 * point cloud scales every dot by the same clamped sqrt(energy) that picks
 * its colour (RaytraceCloud.tsx), so colour is the redundant cue and the
 * geometry is the ordered one. Do not reuse this ramp anywhere the mark's
 * size is constant; use `turbo` with contours, as the surface map does.
 */
export const ARRIVAL_ANCHORS = {
  low: { l: 0.74, c: 0.15, h: 148 },
  mid: { l: 0.82, c: 0.16, h: 88 },
  high: { l: 0.58, c: 0.19, h: 27 },
} as const;

/** Arrival energy -> colour: green at 0, amber at 0.5, red at 1. */
export function arrival(t: number): Rgb {
  return lerpLab3(
    anchorToLab(ARRIVAL_ANCHORS.low),
    anchorToLab(ARRIVAL_ANCHORS.mid),
    anchorToLab(ARRIVAL_ANCHORS.high),
    t,
  );
}

// ── Coverage ─────────────────────────────────────────────────────────────────

/**
 * How opaque a field pixel is at ramp position `t`.
 *
 * Acoustic simulation software maps opaque surfaces; we overlay a pale
 * architectural drawing that the user still has to be able to read and click.
 * A full-range Turbo laid flat would paint dark blue-purple over every quiet
 * part of the room — which is most of it — and bury the thing the field is
 * supposed to be describing.
 *
 * So the bottom of the ramp is transparent: "nothing is happening here" reads
 * as bare paper, not as a cold measurement. `knee` is where colour starts to
 * appear at all and `fade` is how long it takes to reach `max`. Above the knee
 * the ramp is linear — a soft-start curve was tried and made mid-level regions
 * read as weaker than they are, which is the same dishonesty as auto-exposure.
 */
export const FIELD_ALPHA = { knee: 0.06, fade: 0.22, max: 0.85 } as const;

export function fieldAlpha(t: number): number {
  const x = clamp01(t);
  if (x <= FIELD_ALPHA.knee) return 0;
  const ramped = (x - FIELD_ALPHA.knee) / FIELD_ALPHA.fade;
  return Math.min(1, ramped) * FIELD_ALPHA.max;
}

// ── GLSL ─────────────────────────────────────────────────────────────────────

const glslVec4 = (v: readonly number[]) => `vec4(${v.map((n) => n.toFixed(8)).join(', ')})`;
const glslVec2 = (v: readonly number[]) => `vec2(${v.map((n) => n.toFixed(8)).join(', ')})`;

/**
 * `turbo` and `fieldAlpha` as GLSL, GENERATED from the same constants the CPU
 * path evaluates.
 *
 * This is the one structural lesson the volumetric cloud already paid for: its
 * shader was a second, independent transcription of `densityToColor`, which is
 * why `volumetric-render.spec.ts` has to exist at all and why its header spends
 * a paragraph on why a divergence between the two would be invisible to a
 * typecheck. Here there is nothing to transcribe — change `TURBO_POLY` and both
 * paths change together, or neither does.
 *
 * Still worth rendering pixels against the CPU function (the GPU is a second
 * colour-management round trip even when the arithmetic agrees), but that test
 * is now checking colour management rather than guarding a copy-paste.
 */
export const TURBO_GLSL = /* glsl */ `
vec3 turbo(float t) {
  float x = clamp(t, 0.0, 1.0);
  vec4 v4 = vec4(1.0, x, x * x, x * x * x);
  vec2 v2 = vec2(v4.z * v4.z, v4.w * v4.z);
  return clamp(vec3(
    dot(v4, ${glslVec4(TURBO_POLY.r.lo)}) + dot(v2, ${glslVec2(TURBO_POLY.r.hi)}),
    dot(v4, ${glslVec4(TURBO_POLY.g.lo)}) + dot(v2, ${glslVec2(TURBO_POLY.g.hi)}),
    dot(v4, ${glslVec4(TURBO_POLY.b.lo)}) + dot(v2, ${glslVec2(TURBO_POLY.b.hi)}))
  , 0.0, 1.0);
}
`;

export const FIELD_ALPHA_GLSL = /* glsl */ `
float fieldAlpha(float t) {
  float x = clamp(t, 0.0, 1.0);
  return x <= ${FIELD_ALPHA.knee.toFixed(4)}
    ? 0.0
    : min(1.0, (x - ${FIELD_ALPHA.knee.toFixed(4)}) / ${FIELD_ALPHA.fade.toFixed(4)}) * ${FIELD_ALPHA.max.toFixed(4)};
}
`;

const glslVec3 = (v: readonly number[]) => `vec3(${v.map((n) => n.toFixed(8)).join(', ')})`;

/**
 * `thermal` as GLSL, generated from the SAME anchors `THERMAL_ANCHORS`
 * converts to Lab for the CPU path — baked to constants here rather than
 * computing `cos`/`sin` of a hue angle on the GPU every fragment, since the
 * anchors never change at runtime. Everything downstream of the anchors
 * (the piecewise lerp, the Oklab->linear matrix, the sRGB encode) is a
 * direct transcription of `lerpLab3`/`labToRgb` — see TURBO_GLSL's own
 * comment for why generating this instead of hand-writing a second copy is
 * the point, and volumetricThermalGlsl.test.ts for where the two paths are
 * checked against each other.
 */
export const THERMAL_GLSL = /* glsl */ `
vec3 thermalLabToRgb(vec3 lab) {
  float l_ = lab.x + 0.3963377774 * lab.y + 0.2158037573 * lab.z;
  float m_ = lab.x - 0.1055613458 * lab.y - 0.0638541728 * lab.z;
  float s_ = lab.x - 0.0894841775 * lab.y - 1.291485548 * lab.z;
  float l = l_ * l_ * l_;
  float m = m_ * m_ * m_;
  float s = s_ * s_ * s_;
  vec3 lin = clamp(vec3(
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s
  ), 0.0, 1.0);
  vec3 hi = 1.055 * pow(lin, vec3(1.0 / 2.4)) - 0.055;
  return mix(lin * 12.92, hi, step(vec3(0.0031308), lin));
}

vec3 thermal(float t) {
  float x = clamp(t, 0.0, 1.0);
  vec3 low = ${glslVec3(anchorToLab(THERMAL_ANCHORS.low))};
  vec3 mid = ${glslVec3(anchorToLab(THERMAL_ANCHORS.mid))};
  vec3 high = ${glslVec3(anchorToLab(THERMAL_ANCHORS.high))};
  vec3 from = x < 0.5 ? low : mid;
  vec3 to = x < 0.5 ? mid : high;
  float k = x < 0.5 ? x / 0.5 : (x - 0.5) / 0.5;
  return thermalLabToRgb(mix(from, to, k));
}
`;

// ── Particle velocity ────────────────────────────────────────────────────────

/**
 * The one colour that means "the air is moving fast here".
 *
 * Deliberately a single ink rather than a ramp. The particle-velocity cloud
 * encodes speed as PRESENCE, not hue: slow air is not drawn at all, and faster
 * air is drawn more opaquely, so the view reads as "fast is this colour" at a
 * glance instead of asking the viewer to decode a gradient. A five-stop
 * sequential ramp was built and rejected for exactly that reason — every
 * multi-hue option needs a legend before it means anything.
 *
 * Red-orange because it is the standing convention for high velocity magnitude
 * in fluid and aerodynamic visualisation, so it needs no teaching. It survives
 * the light editor ground, where a bright ramp's top end washes out entirely.
 *
 * Distinct from `thermal`'s red end by structure rather than hue: the pressure
 * cloud fills the room through a floor term and spans three anchors, while this
 * one is flat and mostly empty. If the two ever read as the same instrument,
 * swap this for a cool ink (`#00A5C4` was the tested alternative) rather than
 * reintroducing a ramp.
 */
export const VELOCITY_INK = '#E8380D';
